// Unified TOEFL data, tips, and strategies
export const toeflData = {
  studyMap: {
    intro: "Welcome to the TOEFL Companion! These tips and strategies will guide you through Reading, Listening, Speaking, and Writing sections efficiently.",

    reading: [
      "Read the first sentence of each paragraph carefully — it usually contains the main idea.",
      "Read the question first, then go to the passage to locate answers.",
      "Underline keywords in the question and in the passage.",
      "Look for synonyms — the test often rephrases words from the question.",
      "Answers are sequential in most cases — track your progress in the passage.",
      "Manage your time: solve easier questions first, then tackle the harder ones.",
      "Strategy Tip: Skim the passage first to understand the structure, then read deeply only where needed.",
      "Strategy Tip: Eliminate obviously wrong answers before choosing."
    ],

    listening: [
      "Focus entirely on the audio — do not daydream.",
      "Take concise notes: write down main ideas, names, numbers, and transitions.",
      "Identify signal words: 'however', 'in contrast', 'for example', etc.",
      "Campus conversations: student problem → staff response → solution/action.",
      "Lectures: professor background → main idea → supporting details → conclusion.",
      "Strategy Tip: Predict answers while listening; anticipate what the speaker might say next.",
      "Strategy Tip: Do not panic if you miss a word — focus on the next key idea."
    ],

    speaking: {
      delivery: [
        "Speak clearly; accent is secondary to clarity.",
        "Do not mumble; open your mouth and articulate words.",
        "Increase speech volume moderately.",
        "Keep a natural pace; avoid speaking too fast or too slow.",
        "Strategy Tip: Record yourself practicing and identify unclear words or filler sounds."
      ],
      content: [
        "Avoid filler words like 'um', 'ah', 'you know'.",
        "Use TOEFL templates for structure (50% of task).",
        "Include a clear introduction, main points with examples, and conclusion.",
        "Listen carefully and take notes, then plug info into template.",
        "Strategy Tip: Always answer the question directly before adding extra details.",
        "Strategy Tip: Use transition phrases like 'On the other hand', 'For example', 'In conclusion'."
      ]
    },

    writing: {
      integrated: [
        "Write in simple present tense; 250+ words.",
        "Introduction: summarize topic, author's view, lecture counterview.",
        "Body: compare reading points with lecture points; add specific examples.",
        "Conclusion: summarize the differences.",
        "Strategy Tip: Take 3-5 minutes to outline the points before writing.",
        "Strategy Tip: Do not copy phrases directly — paraphrase to show understanding."
      ],
      academicDiscussion: [
        "Manage time: 10 minutes for reading and planning, 15 minutes for writing.",
        "Write 120–150 words.",
        "Introduction: state topic and your opinion clearly.",
        "Body: 2–3 paragraphs, include examples and reasoning.",
        "Acknowledge counter-arguments or alternative viewpoints.",
        "Conclusion: summarize your opinion and main points.",
        "Strategy Tip: Use linking words to connect ideas coherently.",
        "Strategy Tip: Maintain clear paragraph structure with topic sentences."
      ]
    }
  },

  speakingTemplate: [
    "Introduction: State the topic and your opinion clearly.",
    "Body: Give 1–2 reasons with concrete examples.",
    "Conclusion: Restate your opinion confidently."
  ],

  writingIntegratedTemplate: [
    "Intro: General topic + author’s view + lecture counterview.",
    "Body: Compare points from reading with lecture; highlight agreements/disagreements.",
    "Conclusion: Summarize differences and main points."
  ],

  writingIndependentTemplate: [
    "Intro: Introduce topic and clearly state opinion.",
    "Body: 2–3 paragraphs, each with reason + example.",
    "Conclusion: Summarize main points and reinforce opinion."
  ],

  toeflTips: [
    {
      section: "General",
      tips: [
        "50% of your TOEFL score depends on language proficiency; 50% depends on test familiarity.",
        "Plan a systematic approach for Reading, Listening, Speaking, and Writing.",
        "Practice full-length tests under timed conditions.",
        "Familiarize yourself with question types for each section.",
        "Use active vocabulary — learn words in context, not just definitions."
      ]
    },
    {
      section: "Reading",
      tips: [
        "Read the first sentence of every paragraph — main idea often appears here.",
        "Read the question first before scanning the passage.",
        "Circle keywords and look for synonyms in the text.",
        "Answers are mostly in order — keep track of location in passage.",
        "Strategy Tip: Skim passages first, then read deeply where needed.",
        "Time Management Tip: Allocate 1–1.5 minutes per question, and leave buffer for review."
      ]
    },
    {
      section: "Listening",
      tips: [
        "Stay focused and actively listen.",
        "Take concise notes using symbols, abbreviations, and keywords.",
        "Pay attention to tone, emphasis, and signal words.",
        "Identify the structure: problem → solution, cause → effect.",
        "Strategy Tip: Predict what the speaker will say next.",
        "Time Management Tip: Review notes immediately after audio stops to fill gaps."
      ]
    },
    {
      section: "Speaking",
      tips: [
        "Delivery: Speak clearly, moderate pace, articulate words.",
        "Content: Avoid fillers; use template structure for responses.",
        "Strategy Tip: Use transition phrases to link ideas.",
        "Strategy Tip: For independent tasks, plan 15–20 seconds to outline before speaking.",
        "Practice Tip: Record yourself and listen for clarity, pronunciation, and timing."
      ]
    },
    {
      section: "Writing",
      tips: [
        "Integrated: Compare reading and lecture; summarize accurately.",
        "Independent: Present clear opinion with reasons and examples.",
        "Strategy Tip: Outline before writing (3–5 minutes).",
        "Time Management Tip: Spend 20 minutes on writing integrated, 30 minutes on independent.",
        "Focus on clarity, grammar, and coherence rather than complex vocabulary.",
        "Practice Tip: Time yourself and review past essays for improvement."
      ]
    }
  ]
};
